import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, Clock, X } from "lucide-react";
import { TopBar } from "../components/TopBar";
import { AppShell, AppFooter } from "../components/Shell";
import { BottomNav } from "../components/BottomNav";
import { Avatar } from "../components/Avatar";
import { api, errorMessage } from "../lib/api";
import { useT, useLabels } from "../i18n";

const TABS = [
  { id: "matches", key: "tab_matches" },
  { id: "sent", key: "tab_sent" },
  { id: "received", key: "tab_received" },
];

const STATUS = {
  sent: { key: "st_sent", cls: "text-rox-muted border-rox-line" },
  pending: { key: "st_pending", cls: "text-rox-muted border-rox-line" },
  no_reply: { key: "st_no_reply", cls: "text-rox-muted border-rox-line" },
  matched: { key: "match_badge", cls: "text-rox-orange border-rox-orange" },
};

const Row = ({ profile, sub, children, testId, to }) => {
  const inner = (
    <>
      <Avatar name={profile.first_name} src={profile.photo_url} size={56} ring={false} />
      <div className="min-w-0 flex-1">
        <p className="h-display text-2xl">{profile.first_name}</p>
        <p className="truncate text-xs text-rox-muted">{sub}</p>
      </div>
      {children}
    </>
  );
  const cls = "panel flex items-center gap-4 p-3 transition-[border-color] hover:border-rox-orange";
  return to ? <Link to={to} data-testid={testId} className={cls}>{inner}</Link> : <div data-testid={testId} className={cls}>{inner}</div>;
};

export default function Matches() {
  const navigate = useNavigate();
  const { t } = useT();
  const L = useLabels();
  const [tab, setTab] = useState("matches");
  const [matches, setMatches] = useState(null);
  const [sent, setSent] = useState(null);
  const [received, setReceived] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    api.get("/matches").then((r) => setMatches(r.data)).catch(() => setMatches([]));
    api.get("/interests").then((r) => setSent(r.data)).catch(() => setSent([]));
    api.get("/interests/received").then((r) => setReceived(r.data)).catch(() => setReceived([]));
  };
  useEffect(load, []);

  const withdraw = async (id) => {
    setError("");
    try {
      await api.delete(`/interests/${id}`);
      setSent((s) => s.filter((i) => i.id !== id));
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const accept = async (profileId) => {
    setError("");
    try {
      const { data } = await api.post("/interests", { profile_id: profileId });
      if (data.status === "matched") navigate(`/match/${data.match_id}`);
      else load();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const counts = { matches: matches?.length ?? 0, sent: sent?.filter((i) => i.status !== "matched").length ?? 0, received: received?.filter((i) => !i.match_id).length ?? 0 };

  return (
    <AppShell>
      <TopBar back={false} />
      <main className="px-5 pb-28 pt-2 lg:px-8 lg:pb-16" data-testid="matches-page">
        <h1 className="h-display text-4xl">{t("tab_matches")}</h1>
        <div className="mt-5 grid grid-cols-3 gap-1 rounded-[4px] border border-rox-line bg-rox-panel p-1 lg:max-w-xl" data-testid="matches-tabs">
          {TABS.map((tb) => (
            <button key={tb.id} data-testid={`tab-${tb.id}`} data-active={tab === tb.id} onClick={() => setTab(tb.id)}
              className={`flex items-center justify-center gap-1.5 rounded-[3px] py-2 font-display text-sm uppercase tracking-wider transition-[background-color,color] ${tab === tb.id ? "bg-rox-off text-rox-black" : "text-rox-off/70 hover:text-rox-off"}`}>
              {t(tb.key)}
              {counts[tb.id] > 0 && <span className={`rounded-sm px-1.5 text-[10px] font-body font-bold ${tab === tb.id ? "bg-rox-orange text-rox-black" : "bg-rox-gray text-rox-off"}`}>{counts[tb.id]}</span>}
            </button>
          ))}
        </div>
        {error && <p className="mt-3 text-sm text-rox-orange" data-testid="matches-error">{error}</p>}

        {tab === "matches" && (
          <section className="mt-5 stagger" data-testid="matches-list">
            {matches?.length === 0 && (
              <div className="panel p-6" data-testid="no-matches">
                <p className="h-display text-2xl">{t("no_matches_a")} <span className="text-rox-orange">{t("no_matches_b")}</span></p>
                <p className="mt-2 text-sm text-rox-off/75">{t("no_matches_sub")}</p>
                <Link to="/recherche" data-testid="matches-go-search" className="btn-primary mt-5 lg:max-w-xs">{t("search_btn")} <ArrowRight size={18} /></Link>
              </div>
            )}
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {matches?.map((m) => (
                <Row key={m.id} profile={m.partner} sub={`${m.race_name} · ${L.category(m.partner)} · ${m.partner.city}`} testId={`match-item-${m.id}`} to={`/match/${m.id}`}>
                  {m.ready && <span data-testid={`ready-badge-${m.id}`} className="shrink-0 rounded-sm bg-rox-orange px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rox-black">{t("ready")}</span>}
                  <ArrowRight size={18} className="shrink-0 text-rox-orange" />
                </Row>
              ))}
            </div>
          </section>
        )}

        {tab === "sent" && (
          <section className="mt-5 grid grid-cols-1 gap-3 stagger lg:grid-cols-2" data-testid="sent-list">
            {sent?.length === 0 && <p className="text-sm text-rox-muted" data-testid="no-sent">{t("no_sent")}</p>}
            {sent?.map((i) => (
              <Row key={i.id} profile={i.profile} sub={`${i.profile.race_name} · ${i.profile.city}`} testId={`sent-item-${i.id}`}>
                <span className={`flex shrink-0 items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS[i.status]?.cls}`}>
                  {i.status === "matched" ? <Check size={12} /> : <Clock size={12} />} {t(STATUS[i.status]?.key)}
                </span>
                {i.status === "matched" ? (
                  <Link to={`/match/${i.match_id}`} data-testid={`sent-open-${i.id}`} className="shrink-0 text-rox-orange" aria-label={t("a_open_match")}><ArrowRight size={18} /></Link>
                ) : (
                  <button data-testid={`sent-withdraw-${i.id}`} onClick={() => withdraw(i.id)} aria-label={t("withdraw")} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] border border-rox-line text-rox-muted transition-colors hover:border-rox-orange hover:text-rox-orange"><X size={16} /></button>
                )}
              </Row>
            ))}
          </section>
        )}

        {tab === "received" && (
          <section className="mt-5 grid grid-cols-1 gap-3 stagger lg:grid-cols-2" data-testid="received-list">
            {received?.length === 0 && <p className="text-sm text-rox-muted" data-testid="no-received">{t("no_received")}</p>}
            {received?.map((i) => (
              <Row key={i.id} profile={i.profile} sub={`${i.profile.score}% ${t("compatible")} · ${i.profile.race_name} · ${i.profile.city}`} testId={`received-item-${i.id}`}>
                {i.match_id ? (
                  <Link to={`/match/${i.match_id}`} data-testid={`received-open-${i.id}`} className="flex shrink-0 items-center gap-1 rounded-sm border border-rox-orange px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rox-orange"><Check size={12} /> {t("match_badge")}</Link>
                ) : (
                  <button data-testid={`received-accept-${i.id}`} onClick={() => accept(i.profile.id)} className="shrink-0 rounded-[3px] bg-rox-orange px-3 py-2 font-display text-xs uppercase tracking-wider text-rox-black hover:bg-rox-orange-dark">{t("interested_too")}</button>
                )}
              </Row>
            ))}
          </section>
        )}
      </main>
      <AppFooter />
      <BottomNav />
    </AppShell>
  );
}
